import {
  Banner,
  Button,
  Dialog,
  DialogButtons,
  DialogTip,
  EmailsChipInput,
  EyeIcon,
  LinkIcon,
  IconTip,
  Spinner,
  TextArea,
  Tip
} from '@st/theme'
import { match, P } from '@st/pattern'
import { useProcess } from '~/store'
import {
  saveAndCloseClientOrganizer,
  sendClientOrganizer,
  type STSendClientOrganizerMessage,
  type STSendClientOrganizerView
} from './st-send-client-organizer-module'
import { useEmailsManagedBy } from '~/features/app-hooks'
import { selSendClientOrganizerDialog } from './send-client-organizer-selectors'

export function SendClientOrganizerDialog({
  state,
  send,
  onClose
}: {
  state: STSendClientOrganizerView
  send: (message: STSendClientOrganizerMessage) => void
  onClose: () => void
}) {
  const emailsManagedBy = useEmailsManagedBy()
  const route = useProcess('route')
  const model = selSendClientOrganizerDialog(state, emailsManagedBy)

  return (
    <SendClientOrganizerDialogContent
      model={model}
      send={send}
      onSend={() => send(sendClientOrganizer())}
      onClose={onClose}
      onPreview={(url) => route.send({ type: 'openUrl', url })}
    />
  )
}

export function SendClientOrganizerDialogContent({
  model,
  send,
  onSend,
  onClose,
  onPreview
}: {
  model: ReturnType<typeof selSendClientOrganizerDialog>
  send: (message: STSendClientOrganizerMessage) => void
  /** The primary button; delivery continues to its invoice step instead of sending. */
  onSend: () => void
  onClose: () => void
  onPreview: (url: string) => void
}) {
  return match(model)
    .with({ kind: P.union('loading', 'loadFailed') }, (model) => (
      <Dialog
        title={model.title}
        className="w-full max-w-2xl"
        buttons={
          <DialogButtons>
            <Button variant="subtle" onClick={onClose}>
              Close
            </Button>
          </DialogButtons>
        }
      >
        {match(model.kind)
          .with('loading', () => (
            <div className="flex items-center justify-center py-16">
              <Spinner className="size-8 fill-blue-500 text-blue-100" />
            </div>
          ))
          .with('loadFailed', () => (
            <div className="flex flex-col gap-2 py-8 text-sm text-gray-700">
              <div className="font-semibold text-gray-900">Failed to load email draft.</div>
              <div>Please close this dialog and try again.</div>
            </div>
          ))
          .exhaustive()}
      </Dialog>
    ))
    .with({ kind: 'email' }, (model) => (
      <SendClientOrganizerDialogView
        model={model}
        send={send}
        onSend={onSend}
        onClose={onClose}
        onPreview={onPreview}
      />
    ))
    .exhaustive()
}

function SendClientOrganizerDialogView({
  model,
  onClose,
  onPreview,
  send,
  onSend
}: {
  model: Extract<ReturnType<typeof selSendClientOrganizerDialog>, { kind: 'email' }>
  send: (message: STSendClientOrganizerMessage) => void
  onSend: () => void
  onClose: () => void
  onPreview: (url: string) => void
}) {
  return (
    <Dialog
      title={model.title}
      subtitle={model.subtitle}
      className="w-full max-w-2xl"
      tip={
        <>
          {model.showDeliveryReminder && (
            <Banner variant="info" className="text-left text-sm">
              Please review the automated summary page and signature fields before delivering the
              return.
            </Banner>
          )}
          {model.karbonNotice != undefined && <DialogTip>{model.karbonNotice}</DialogTip>}
        </>
      }
      buttons={
        <DialogButtons
          leftButton={
            <Tip title={model.copyLinkTip}>
              <Button
                variant="default"
                leadingIcon={<LinkIcon className="h-4 w-4" />}
                disabled={model.busy}
                onClick={() =>
                  send({
                    type: 'copyQuestionnaireLinkRequested',
                    url: model.state.sendState.questionnaireUrl
                  })
                }
              >
                Copy shareable link
              </Button>
            </Tip>
          }
        >
          <Button
            variant="subtle"
            disabled={model.busy}
            onClick={match(model.saveOnClose)
              .with(true, () => () => send(saveAndCloseClientOrganizer()))
              .with(false, () => onClose)
              .exhaustive()}
          >
            Close
          </Button>

          {model.previewUrl != undefined && (
            <Tip title="Preview organizer">
              <Button
                variant="default"
                leadingIcon={<EyeIcon className="h-4 w-4" />}
                disabled={model.busy}
                onClick={() => onPreview(model.previewUrl!)}
              >
                Preview organizer
              </Button>
            </Tip>
          )}

          <Button variant="primary" disabled={!model.canSend} onClick={onSend}>
            {model.sendLabel}
          </Button>
        </DialogButtons>
      }
    >
      <div className="flex flex-col gap-2">
        <div className="flex flex-col gap-2">
          <label className="font-semibold">To:</label>
          <EmailsChipInput
            className="w-full"
            readOnly={model.readOnly}
            emails={model.emails}
            inputValue={model.state.inputValue}
            onInputChange={(email) => send({ type: 'emailTyped', email })}
            selectedEmail={model.state.selectedEmail}
            onSelectedEmailChange={(email) => send({ type: 'emailSelected', email })}
            onAdd={(email) => send({ type: 'emailAdded', email })}
            onDelete={(email) => send({ type: 'emailRemoved', email })}
          />
        </div>
        <div className="flex flex-col gap-2">
          <div className="flex items-center gap-2">
            <label className="font-semibold">Subject:</label>
            <IconTip
              icon="info"
              body="You can change the subject in Settings → Templates"
              className="text-gray-500"
            />
          </div>
          <div className="rounded-lg px-3 py-2 text-gray-900 ring-1 ring-inset ring-gray-200">
            {model.state.sendState.subject}
          </div>
        </div>
        <div className="flex flex-col gap-2">
          <label className="font-semibold">Message:</label>
          <TextArea
            className="min-h-96"
            placeholder="Send a message"
            readOnly={model.readOnly}
            value={model.state.body}
            onChange={(body) => send({ type: 'bodyChanged', body })}
          />
        </div>
      </div>
    </Dialog>
  )
}
